import { Button } from "@/components/ui/button"
import { MultiCombobox, ComboboxTrigger } from "@/components/ui/combobox"
import { SheetPageBody, SheetPageTitle } from "@/components/ui/sheet-page"
import { ChevronsUpDown, Trash2, Users } from "lucide-react"
import { useId, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useProps } from "@/lib/page-rendering"

type PageProps = {
  students?: SelectOption<number>[]
}
export default function Page() {
  const { students } = useProps<PageProps>()
  const navigate = useNavigate()
  const { t } = useTranslation(["preference_templates", "common"])
  const [selectedStudents, setSelectedStudents] = useState<
    Array<SelectOption<number>>
  >([])

  const comboboxLabelId = useId()

  if (!students) {
    return null
  }

  const submitHandler = () => {
    navigate({
      href: ".",
      method: "post",
      data: {
        students: selectedStudents.map((student) => student.value),
      },
    }).then(({ errors }) => {
      if (Object.keys(errors).length === 0) {
        window.parent.postMessage("sheet:reload_on_close")
        window.parent.postMessage("sheet:close")
      }
    })
  }

  return (
    <SheetPageBody>
      <div className="bg-background sticky top-0 inline-flex flex-col space-y-2 border-b pb-1">
        <SheetPageTitle icon={Users}>
          {t("applications.new.title")}
        </SheetPageTitle>
        <span className="text-muted-foreground text-sm">
          {t("applications.new.description")}
        </span>
      </div>
      <div className="flex flex-col gap-4 pt-1">
        <MultiCombobox
          options={students}
          selected={selectedStudents}
          onChange={setSelectedStudents}
          renderComboboxTrigger={({ open }) => (
            <ComboboxTrigger asChild open={open}>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="h-full w-full flex-wrap justify-start"
                aria-labelledby={comboboxLabelId}
              >
                <div
                  className="flex grow items-center justify-between"
                  id={comboboxLabelId}
                >
                  {t("applications.new.combobox.select")}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </div>
              </Button>
            </ComboboxTrigger>
          )}
        />
        <ul
          className="flex flex-col gap-2"
          aria-label={t("applications.new.selected.label")}
        >
          {selectedStudents.map((student) => (
            <li
              key={student.value}
              className="flex items-center justify-between"
              aria-label={student.label}
            >
              {student.label}
              <Button
                variant={"destructive-outline"}
                onClick={() =>
                  setSelectedStudents((selectedStudents) =>
                    selectedStudents.filter((s) => s.value !== student.value),
                  )
                }
              >
                <Trash2 />
                {t("common:actions.remove")}
              </Button>
            </li>
          ))}
        </ul>
        <div className="bg-background sticky bottom-0 w-full border-t py-4">
          <Button
            className="w-full"
            disabled={selectedStudents.length === 0}
            onClick={submitHandler}
          >
            {t("applications.new.apply")}
          </Button>
        </div>
      </div>
    </SheetPageBody>
  )
}
